import { Routes } from '@angular/router';

import { AppShellComponent } from '@app/layout/app-shell.component';
import { anonymousOnlyGuard } from '@app/core/guards/anonymous-only.guard';
import { authenticatedOnlyGuard } from '@app/core/guards/authenticated-only.guard';
import { roleGuard } from '@app/core/guards/role.guard';
import { ownerLiveGuard } from '@app/core/guards/owner-live.guard';
import { historicalImportDataDropGuard } from '@app/features/historical-imports/historical-import-data-drop.guard';
import { registrationReplayExitGuard } from '@app/core/analytics/registration-replay-exit.guard';

export const routes: Routes = [
  {
    path: 'integrations/hostfully/connect', title: 'Connect Hostfully | Action Ledgers',
    loadComponent: () => import('@app/features/pms/pages/hostfully-onboarding.page').then(m => m.HostfullyOnboardingPageComponent),
    data: { title: 'Connect Hostfully' }
  },
  {
    path: 'upload/import/:token',
    loadComponent: () =>
      import('@app/features/historical-imports/historical-import-public-upload.page').then(
        (m) => m.HistoricalImportPublicUploadPageComponent
      ),
    data: { title: 'Secure Historical Data Upload' }
  },
  {
    path: 'login',
    canActivate: [anonymousOnlyGuard],
    loadComponent: () =>
      import('@app/features/auth/login.page').then((m) => m.LoginPageComponent),
    data: {
      title: 'Login'
    }
  },
  {
    path: 'register',
    canActivate: [anonymousOnlyGuard],
    canDeactivate: [registrationReplayExitGuard],
    loadComponent: () =>
      import('@app/features/auth/register.page').then((m) => m.RegisterPageComponent),
    data: {
      title: 'Register'
    }
  },
  {
    path: 'register/pms',
    canActivate: [authenticatedOnlyGuard],
    loadComponent: () =>
      import('@app/features/auth/registration-pms.page').then((m) => m.RegistrationPmsPageComponent),
    data: {
      title: 'Connect Property System'
    }
  },
  {
    path: 'owner-invitations/:token',
    canActivate: [anonymousOnlyGuard],
    loadComponent: () =>
      import('@app/features/auth/owner-invitation.page').then((m) => m.OwnerInvitationPageComponent),
    data: { title: 'Accept Owner Invitation' }
  },
  {
    path: 'owner-claims/:token',
    canActivate: [anonymousOnlyGuard],
    loadComponent: () =>
      import('@app/features/auth/owner-claim.page').then((m) => m.OwnerClaimPageComponent),
    data: { title: 'Claim Owner Account' }
  },
  {
    path: 'billing/success',
    canActivate: [authenticatedOnlyGuard],
    loadComponent: () =>
      import('@app/features/auth/registration-checkout-return.page').then(
        (m) => m.RegistrationCheckoutReturnPageComponent
      ),
    data: {
      title: 'Confirm Payment'
    }
  },
  {
    path: 'billing/canceled',
    canActivate: [authenticatedOnlyGuard],
    loadComponent: () =>
      import('@app/features/auth/registration-checkout-return.page').then(
        (m) => m.RegistrationCheckoutReturnPageComponent
      ),
    data: {
      title: 'Checkout Canceled',
      checkoutCancelled: true
    }
  },
  {
    path: 'subscription/reactivate',
    canActivate: [authenticatedOnlyGuard],
    loadComponent: () =>
      import('@app/features/auth/registration-pms.page').then((m) => m.RegistrationPmsPageComponent),
    data: {
      title: 'Restore Subscription',
      subscriptionRecovery: true
    }
  },
  {
    path: 'check-email',
    loadComponent: () =>
      import('@app/features/auth/check-email.page').then((m) => m.CheckEmailPageComponent),
    data: {
      title: 'Check Email'
    }
  },
  {
    path: 'confirm-email',
    loadComponent: () =>
      import('@app/features/auth/email-confirmation-result.page').then(
        (m) => m.EmailConfirmationResultPageComponent
      ),
    data: {
      title: 'Email Confirmation'
    }
  },
  {
    path: 'forgot-password',
    canActivate: [anonymousOnlyGuard],
    loadComponent: () =>
      import('@app/features/auth/forgot-password.page').then(
        (m) => m.ForgotPasswordPageComponent
      ),
    data: {
      title: 'Forgot Password'
    }
  },
  {
    path: 'reset-password',
    canActivate: [anonymousOnlyGuard],
    loadComponent: () =>
      import('@app/features/auth/reset-password.page').then(
        (m) => m.ResetPasswordPageComponent
      ),
    data: {
      title: 'Reset Password'
    }
  },
  {
    path: 'change-password',
    canActivate: [authenticatedOnlyGuard],
    loadComponent: () =>
      import('@app/features/auth/change-password.page').then(
        (m) => m.ChangePasswordPageComponent
      ),
    data: {
      title: 'Change Password'
    }
  },
  {
    path: 'verify/certification/:certificationDocumentId',
    loadComponent: () =>
      import('@app/features/certification-verification/certification-verification.page').then(
        (m) => m.CertificationVerificationPageComponent
      ),
    data: {
      title: 'Verify Certification'
    }
  },  {
    path: 'owner-actions',
    loadComponent: () =>
      import('@app/features/owner-actions/owner-actions.page').then(
        (m) => m.OwnerActionsPageComponent
      ),
    data: {
      title: 'Secure Owner Action'
    }
  },
  {
    path: 'resend-confirmation',
    loadComponent: () =>
      import('@app/features/auth/resend-confirmation.page').then(
        (m) => m.ResendConfirmationPageComponent
      ),
    data: {
      title: 'Resend Confirmation'
    }
  },
  {
    path: 'auth/external/callback',
    loadComponent: () =>
      import('@app/features/auth/external-auth-callback.page').then(
        (m) => m.ExternalAuthCallbackPageComponent
      ),
    data: {
      title: 'External Sign-In'
    }
  },
  {
    path: 'auth/external/error',
    loadComponent: () =>
      import('@app/features/auth/external-auth-callback.page').then(
        (m) => m.ExternalAuthCallbackPageComponent
      ),
    data: {
      title: 'External Sign-In Error'
    }
  },
  {
    path: 'auth/register/complete',
    loadComponent: () =>
      import('@app/features/auth/external-registration-complete.page').then(
        (m) => m.ExternalRegistrationCompletePageComponent
      ),
    data: {
      title: 'Complete Registration'
    }
  },
  {
    path: 'verify/certification/:certificationDocumentId',
    loadComponent: () =>
      import('@app/features/certification-verification/certification-verification.page').then(
        (m) => m.CertificationVerificationPageComponent
      ),
    data: {
      title: 'Verify Certification'
    }
  },  {
    path: 'owner-actions',
    loadComponent: () =>
      import('@app/features/owner-actions/owner-actions.page').then(
        (m) => m.OwnerActionsPageComponent
      ),
    data: {
      title: 'Owner Actions'
    }
  },  {
    path: 'onboarding',
    redirectTo: 'dashboard',
    pathMatch: 'full',
    data: {
      title: 'Owner Onboarding',
      roles: ['owner']
    }
  },
  {
    path: '',
    component: AppShellComponent,
    canActivate: [authenticatedOnlyGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard'
      },
      {
        path: 'owner/blocked',
        canActivate: [roleGuard],
        loadComponent: () =>
          import('@app/features/owner/owner-blocked-access.page').then(
            (m) => m.OwnerBlockedAccessPageComponent
          ),
        data: {
          title: 'Limited Access',
          breadcrumb: 'Limited Access',
          roles: ['owner']
        }
      },
      {
        path: 'dashboard',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/dashboard/operations-dashboard.page').then((m) => m.OperationsDashboardPageComponent),
        data: {
          title: 'Operations Dashboard',
          breadcrumb: 'Dashboard',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'properties',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/properties/properties-hub.page').then(
            (m) => m.PropertiesHubPageComponent
          ),
        data: {
          title: 'Properties',
          breadcrumb: 'Properties',
          roles: ['owner', 'admin']
        }
      },
      {
        path: 'addons',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/addons/addons.page').then((m) => m.AddonsPageComponent),
        data: {
          title: 'Addons',
          breadcrumb: 'Addons',
          roles: ['owner']
        }
      },
      {
        path: 'signals',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/signals/signals.page').then((m) => m.SignalsPageComponent),
        data: {
          title: 'Decision AI Engine',
          breadcrumb: 'Decision AI',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'market-overview',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/market/market-overview.page').then(
            (m) => m.MarketOverviewPageComponent
          ),
        data: {
          title: 'Market Intelligence',
          breadcrumb: 'Market Overview',
          roles: ['owner', 'admin']
        }
      },
      {
        path: 'performance',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/performance/performance.page').then(
            (m) => m.PerformancePageComponent
          ),
        data: {
          title: 'Property Performance',
          breadcrumb: 'Performance',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'email-queue',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/email-queue/email-queue.page').then(
            (m) => m.EmailQueuePageComponent
          ),
        data: {
          title: 'Email Instruction Queue',
          breadcrumb: 'Email Queue',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'action-queue',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/action-queue/action-queue.page').then(
            (m) => m.ActionQueuePageComponent
          ),
        data: {
            title: 'Action Queue',
          breadcrumb: 'Action Queue',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'manual-actions',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/manual-actions/manual-actions.page').then(
            (m) => m.ManualActionsPageComponent
          ),
        data: {
          title: 'Add Manually',
          breadcrumb: 'Add Manually',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'help',
        canActivate: [roleGuard],
        loadComponent: () =>
          import('@app/features/help/help-center.page').then(
            (m) => m.HelpCenterPageComponent
          ),
        data: {
          title: 'Help & FAQ',
          breadcrumb: 'Help & FAQ',
          roles: ['owner', 'accountant']
        }
      },
      {
        path: 'bookings',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/bookings/bookings.page').then((m) => m.BookingsPageComponent),
        data: {
          title: 'Booking Exceptions',
          breadcrumb: 'Bookings',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'messages',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/messages/messages.page').then((m) => m.MessagesPageComponent),
        data: {
          title: 'Guest Communication Inbox',
          breadcrumb: 'Messages',
          roles: ['owner', 'admin']
        }
      },
      {
        path: 'guest-messaging/reviews/:reviewId',
        canActivate: [roleGuard],
        loadComponent: () =>
          import('@app/features/guest-messaging/guest-messaging-review.page').then(
            (m) => m.GuestMessagingReviewPageComponent
          ),
        data: {
          title: 'Review Guest Reply',
          breadcrumb: 'Guest Reply Review',
          roles: ['owner']
        }
      },
      {
        path: 'ops',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/ops/ops.page').then((m) => m.OpsPageComponent),
        data: {
          title: 'Operations Tickets',
          breadcrumb: 'Operations',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'weekly-review',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/reviews/weekly-review.page').then(
            (m) => m.WeeklyReviewPageComponent
          ),
        data: {
          title: 'Weekly Review',
          breadcrumb: 'Weekly Review',
          roles: ['owner']
        }
      },
      {
        path: 'certifications/review',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('./features/compliance/monthly-certification-review.page').then(
            (m) => m.MonthlyCertificationReviewPageComponent
          ),
        data: {
          title: 'Certification Review',
          breadcrumb: 'Certification Review',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'certifications',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/compliance/monthly-certification.page').then(
            (m) => m.MonthlyCertificationPageComponent
          ),
        data: {
          title: 'Certification',
          breadcrumb: 'Certification',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'compliance',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/compliance/compliance-hub.page').then(
            (m) => m.ComplianceHubPageComponent
          ),
        data: {
          title: 'Compliance Hub',
          breadcrumb: 'Compliance',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'reports',
        pathMatch: 'full',
        redirectTo: 'dashboard'
      },
      {
        path: 'settings',
        canActivate: [roleGuard, ownerLiveGuard],
        data: {
          roles: ['owner', 'admin', 'accountant']
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@app/features/settings/settings.page').then((m) => m.SettingsPageComponent),
            data: {
              title: 'Settings',
              breadcrumb: 'Settings'
            }
          },
          {
            path: 'integrations',
            children: [
              {
                path: 'pms',
                loadComponent: () =>
                  import('@app/features/pms/pages/pms-dashboard.page').then(
                    (m) => m.PmsDashboardPageComponent
                  ),
                data: {
                  title: 'PMS Integrations',
                  breadcrumb: 'PMS Integrations'
                }
              },
              {
                path: 'pms/connect/ownerrez',
                canActivate: [roleGuard],
                loadComponent: () =>
                  import('@app/features/pms/pages/ownerrez-connect.page').then(
                    (m) => m.OwnerRezConnectPageComponent
                  ),
                data: {
                  title: 'Connect OwnerRez',
                  breadcrumb: 'Connect OwnerRez',
                  roles: ['owner']
                }
              },
              {
                path: 'pms/connect/ownerrez/callback',
                loadComponent: () =>
                  import('@app/features/pms/pages/ownerrez-callback.page').then(
                    (m) => m.OwnerRezCallbackPageComponent
                  ),
                data: {
                  title: 'OwnerRez Callback',
                  breadcrumb: 'OwnerRez Callback'
                }
              },
              {
                path: 'pms/connect/beds24',
                canActivate: [roleGuard],
                loadComponent: () =>
                  import('@app/features/pms/pages/beds24-connect.page').then(
                    (m) => m.Beds24ConnectPageComponent
                  ),
                data: {
                  title: 'Beds24 connection',
                  breadcrumb: 'Beds24',
                  roles: ['owner']
                }
              },
              {
                path: 'pms/connections/:connectionId',
                loadComponent: () =>
                  import('@app/features/pms/pages/pms-connection-detail.page').then(
                    (m) => m.PmsConnectionDetailPageComponent
                  ),
                data: {
                  title: 'PMS Connection',
                  breadcrumb: 'PMS Connection'
                }
              }
            ]
          }
        ]
      },
      {
        path: 'properties/:propertyId/ai-guest-messaging/knowledge',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/guest-messaging/property-knowledge.page').then(
            (m) => m.PropertyKnowledgePageComponent
          ),
        data: {
          title: 'AI Property Knowledge',
          breadcrumb: 'AI Property Knowledge',
          roles: ['owner']
        }
      },
      {
        path: 'properties/:propertyId/pms',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/pms/pages/property-pms-config.page').then(
            (m) => m.PropertyPmsConfigPageComponent
          ),
        data: {
          title: 'Property PMS Settings',
          breadcrumb: 'Property PMS',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'properties/:propertyId/pms/sync',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/pms/pages/property-pms-sync.page').then(
            (m) => m.PropertyPmsSyncPageComponent
          ),
        data: {
          title: 'PMS Sync Status',
          breadcrumb: 'PMS Sync',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'properties/:propertyId/pms/events',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/pms/pages/property-pms-events.page').then(
            (m) => m.PropertyPmsEventsPageComponent
          ),
        data: {
          title: 'PMS Event Log',
          breadcrumb: 'PMS Events',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'pms/write-approvals',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/pms/pages/pms-write-approvals.page').then(
            (m) => m.PmsWriteApprovalsPageComponent
          ),
        data: {
          title: 'Pending PMS Write Approvals',
          breadcrumb: 'PMS Write Approvals',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'pms/write-approvals/:writeRequestId',
        canActivate: [roleGuard, ownerLiveGuard],
        loadComponent: () =>
          import('@app/features/pms/pages/pms-write-approval-detail.page').then(
            (m) => m.PmsWriteApprovalDetailPageComponent
          ),
        data: {
          title: 'PMS Write Approval',
          breadcrumb: 'PMS Write Approval',
          roles: ['owner', 'admin', 'accountant']
        }
      },
      {
        path: 'imports',
        canActivate: [historicalImportDataDropGuard],
        loadComponent: () =>
          import('@app/features/historical-imports/account-import-status.page').then(
            (m) => m.AccountImportStatusPageComponent
          ),
        data: { title: 'Import Status', breadcrumb: 'Imports' }
      },
      {
        path: 'imports/:orderId/reports',
        canActivate: [historicalImportDataDropGuard],
        loadComponent: () =>
          import('@app/features/historical-imports/historical-import-data-drop-reports.page').then(
            (m) => m.HistoricalImportDataDropReportsPageComponent
          ),
        data: { title: 'Import Reports', breadcrumb: 'Import Reports', requiredCapability: 'canViewReports' }
      },
      {
        path: 'imports/:orderId',
        canActivate: [historicalImportDataDropGuard],
        loadComponent: () =>
          import('@app/features/historical-imports/account-import-status.page').then(
            (m) => m.AccountImportStatusPageComponent
          ),
        data: { title: 'Import Status', breadcrumb: 'Import Status' }
      },
      {
        path: 'partner',
        canActivate: [roleGuard],
        loadChildren: () =>
          import('@app/features/partner/partner.routes').then((m) => m.PARTNER_ROUTES),
        data: {
          title: 'Partner Dashboard',
          breadcrumb: 'Partner',
          roles: ['partner']
        }
      },
      {
        path: 'admin',
        canActivate: [roleGuard],
        loadChildren: () =>
          import('@app/features/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
        data: {
          title: 'Admin Dashboard',
          breadcrumb: 'Admin',
          roles: ['admin']
        }
      }
    ]
  },
  {
    path: '**',
    redirectTo: ''
  }
];
